import { Alert, Button, Card, PasswordInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChangePassword } from 'src/Hooks/Mutations/useChangePassword';

import styles from './ChangePasswordSection.module.scss';

const ChangePasswordSection = () => {
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);

  const form = useForm({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      oldPassword: (value: string) =>
        value.length === 0 ? t('forms.required', 'Ce champ est requis') : null,
      newPassword: (value: string) => {
        if (value.length < 8)
          return t('forms.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères');
        if (!/[A-Z]/.test(value))
          return t(
            'forms.passwordNeedsUppercase',
            'Le mot de passe doit contenir au moins une majuscule'
          );
        if (!/[a-z]/.test(value))
          return t(
            'forms.passwordNeedsLowercase',
            'Le mot de passe doit contenir au moins une minuscule'
          );
        if (!/[0-9]/.test(value))
          return t(
            'forms.passwordNeedsNumber',
            'Le mot de passe doit contenir au moins un chiffre'
          );
        return null;
      },
      confirmPassword: (value: string, values) =>
        value === values.newPassword
          ? null
          : t('forms.passwordsDoNotMatch', 'Les mots de passe ne correspondent pas'),
    },
  });

  const { mutate, isLoading } = useChangePassword({
    onSuccess: () => {
      setSuccess(true);
      form.reset();
    },
    onError: (error: unknown) => {
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'WRONG_PASSWORD') {
        form.setFieldError(
          'oldPassword',
          t('profile.changePassword.wrongPassword', 'Mot de passe actuel incorrect')
        );
      }
    },
  });

  return (
    <Card withBorder radius="md" p="xl" className={styles.container}>
      <div className={styles.title}>
        {t('profile.changePassword.title', 'Changer le mot de passe')}
      </div>
      <form
        onSubmit={form.onSubmit(values =>
          mutate({ oldPassword: values.oldPassword, newPassword: values.newPassword })
        )}
      >
        <div className={styles.fields}>
          <PasswordInput
            label={t('profile.changePassword.oldPassword', 'Mot de passe actuel')}
            placeholder="············"
            {...form.getInputProps('oldPassword')}
          />
          <PasswordInput
            label={t('profile.changePassword.newPassword', 'Nouveau mot de passe')}
            placeholder="············"
            {...form.getInputProps('newPassword')}
          />
          <PasswordInput
            label={t('profile.changePassword.confirmPassword', 'Confirmer le nouveau mot de passe')}
            placeholder="············"
            {...form.getInputProps('confirmPassword')}
          />
        </div>
        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" className={styles.alert}>
            {t('profile.changePassword.success', 'Mot de passe modifié avec succès')}
          </Alert>
        )}
        <Button type="submit" loading={isLoading} className={styles.submitButton}>
          {t('profile.changePassword.submit', 'Changer le mot de passe')}
        </Button>
      </form>
    </Card>
  );
};

export default ChangePasswordSection;
